import axios from 'axios'

import { transformQueryData } from '@/clients/offer-parse-utils'
import { env } from '@/env.mjs'
import { ERROR_TRANSLATIONS, ErrorResponse } from '@/lib'
import {
  CategoriesResponse,
  categoriesResponseSchema,
  ConfigurationResponse,
  ContentResponse,
  ReservationPolicy,
  ReservationResponse,
  UnitsResponse,
} from '@/types'

import {
  ConditionsResponse,
  ConditionsResponseSchema,
} from './product-conditions'
import { OfferListItem, OfferListResponse } from './product-list'

type FetchProductListConfig = {
  countryCode: string
  languageCode: string
  params?: {
    unitIds?: string
    page?: string
    size?: string
    search?: string | null
    categories?: string | null
    productConditions?: string | null
  }
}

type MarketParams = {
  country_code: string
  language_code: string
}

type GetOfferParams2 = {
  offerNumber: string
  countryCode: string
  languageCode: string
}

const handleReservationErrors = (error: any) => {
  const data: ErrorResponse = error.response?.data
  const errorCode = data?.code
  data.message = ERROR_TRANSLATIONS[errorCode]
  throw data
}

export const api = {
  axiosInstance: axios.create({
    baseURL: `${env.NEXT_PUBLIC_APP_URL}`,
  }),
  configuration: {
    get: async ({ country_code, language_code }: MarketParams) => {
      return api.axiosInstance.get<ConfigurationResponse>(
        `/config/${country_code}/${language_code}`
      )
    },
  },

  conditions: {
    get: async ({
      countryCode,
      languageCode,
    }: {
      countryCode: string
      languageCode: string
    }) => {
      const url = `${env.NEXT_PUBLIC_ROOT_URL}/product-conditions/${countryCode}/${languageCode}`
      const response = await fetch(url)

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`)
      }

      const responseData = await response.json()

      try {
        return ConditionsResponseSchema.parse(responseData)
      } catch (e) {
        console.log('failed parsing conditions response:', e)
        return responseData as ConditionsResponse
      }
    },
  },
  markets: {
    get: async () => {
      const response = await api.axiosInstance.get<{ markets: string[] }>(
        `${env.NEXT_PUBLIC_ROOT_URL}/markets`
      )
      return response.data
    },
  },
  groupedOffers: {
    get: async ({
      config: {
        languageCode,
        params = {
          size: '16',
        },
      },
      pageParam,
    }: {
      config: FetchProductListConfig
      pageParam: number
    }) => {
      const url = new URL(
        `${env.NEXT_PUBLIC_ROOT_URL}/offers/grouped/search?languageCode=${languageCode}`
      )
      for (const [key, val] of Object.entries({
        ...params,
        page: pageParam.toString(),
      })) {
        if (val) {
          url.searchParams.append(key, val)
        }
      }

      const response = await api.axiosInstance.get<OfferListResponse>(
        url.toString()
      )
      const productMap = new Map<number, OfferListItem>()
      for (const product of response.data.content) {
        productMap.set(product.offers[0].id, product)
      }
      const productList = response.data.content.map(
        ({ offers }) => offers[0].id
      )
      return { response, productMap, productList }
    },
  },
  stores: {
    get: async (country_code: string) => {
      const url = `/stores/${country_code}`
      const response = await api.axiosInstance.get<UnitsResponse>(url)
      return response.data
    },
  },
  translation: {
    get: async ({ country_code, language_code }: MarketParams) => {
      return api.axiosInstance.get<ContentResponse>(
        `/translation/${country_code}/${language_code}/public`
      )
    },
  },
  categories: {
    get: async ({
      unitId,
      countryCode,
      languageCode,
    }: {
      unitId: string
      countryCode: string
      languageCode: string
    }) => {
      const url = `${env.NEXT_PUBLIC_APP_URL}/categories/${countryCode}/${languageCode}/${unitId}`

      const response = await fetch(url)

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`)
      }

      const json = await response.json()

      try {
        return categoriesResponseSchema.parse(json)
      } catch (e) {
        console.log('failed parsing categories response:', e)
        return json as CategoriesResponse
      }
    },
  },
  featureFlags: {
    get: async ({ countryCode }: { countryCode: string }) => {
      return api.axiosInstance.get<Record<string, boolean>>(
        `${env.NEXT_PUBLIC_ROOT_URL}/configuration/feature-flags/${countryCode}`
      )
    },
  },
  reservationPolicy: {
    get: async ({ countryCode }: { countryCode: string }) => {
      const response = await axios.get<ReservationPolicy>(
        `${env.NEXT_PUBLIC_ROOT_URL}/reservation-policies/${countryCode}`
      )
      return response.data
    },
  },
  offers: {
    get: async ({
      offerNumber,
      countryCode,
      languageCode,
    }: GetOfferParams2) => {
      const url = `${env.NEXT_PUBLIC_APP_URL}/offers/${countryCode}/${languageCode}/${offerNumber}`

      const response = await fetch(url)

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`)
      }

      const offer = await response.json()
      return transformQueryData(offer)
    },
  },
  reservations: {
    post: async (
      state: {
        offerId: string
        offerNumber: string
        storeNumber: string
        authToken: string
      },
      locale: string
    ) => {
      try {
        const turnstileToken = window.ikea?.recommerce?.latestTurnstileToken
        const headers: Record<string, string> = {
          Authorization: `Bearer ${state.authToken}`,
          'X-Market-Locale': locale,
        }

        if (turnstileToken) {
          headers['x-cf-turnstile'] = turnstileToken
        }

        const { authToken: _, ...stateWithoutAuth } = state
        return await axios.post(
          `${process.env.NEXT_PUBLIC_ROOT_URL}/reservations`,
          { ...stateWithoutAuth },
          { headers }
        )
      } catch (error: any) {
        handleReservationErrors(error)
        console.error('Reservation error:', error)
      }
    },
    get: async ({
      offerNumber,
      authToken,
      locale,
    }: {
      offerNumber: string
      authToken: string
      locale: string
    }) => {
      try {
        const turnstileToken = window.ikea?.recommerce?.latestTurnstileToken
        const headers: Record<string, string> = {
          Authorization: `Bearer ${authToken}`,
          'X-Market-Locale': locale,
        }

        if (turnstileToken) {
          headers['x-cf-turnstile'] = turnstileToken
        }

        const response = await api.axiosInstance.get<ReservationResponse>(
          `${process.env.NEXT_PUBLIC_ROOT_URL}/offers/reservation/offer/${offerNumber}`,
          { headers }
        )
        return response.data
      } catch (error: any) {
        return Promise.reject(error)
      }
    },
    delete: async ({
      offerId,
      reservationId,
      authToken,
      locale,
    }: {
      offerId?: string
      reservationId: string
      authToken: string
      locale: string
    }) => {
      const headers: Record<string, string> = {
        Authorization: `Bearer ${authToken}`,
        'X-Market-Locale': locale,
      }
      // Only send body when offerId is available (non-proxied countries)
      const data = offerId ? { offerId, reservationId } : undefined
      return await axios.delete(
        `${process.env.NEXT_PUBLIC_ROOT_URL}/reservations/${reservationId}`,
        {
          data,
          headers,
        }
      )
    },
  },
}
